import React, { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { checkInputErrorRequired, formatDNI, useInput, useSelect } from '@chilecompra/react-kit'
import { Button, Card, Input, Pagination, Select, Skeleton, Table, TableCell } from '@chilecompra/react-kit/components'
import {
  PROVIDER_IDENTIFIER_KEYS_DICTIONARY,
  PROVIDER_IDENTIFIER_LABELS_DICTIONARY,
  PROVIDER_IDENTIFIER_OPTIONS,
  PROVIDER_ROWS
} from '../../config/settings/constants'
import {
  onAddSellerSuccess as onAddSpecificProviderSuccess,
  onRemoveSellerSuccess as onRemoveSpecificProviderSuccess
} from '../QuoteStepTwo/QuoteStepTwo.actions'
import { onGetSpecificProvidersThunk } from './QuoteProvidersList.actions'
import {
  Action,
  ActionDelete,
  ActionIcon,
  ActionText,
  BackButton,
  BackIcon,
  BackLink,
  BackText,
  CardDescription,
  CardImage,
  CardTitle,
  Description,
  Form,
  GridContainer,
  GridItem,
  RecordText,
  Results,
  Root,
  TableRow,
  Title
} from './QuoteProvidersList.styles'

/**
 * The QuoteProvidersList's container.
 */
const QuoteProvidersList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const initialStateRef = useRef(true)
  const { loading, page, records, totalPages, totalRecords } = useSelector(state => state.quoteProvidersList)
  const { sellersAdded: selectedProviders } = useSelector(state => state.quoteStepTwo)
  const { value: identifierTypeValue, onChange: handleChangeIdentifierType } = useSelect({
    initialValue: PROVIDER_IDENTIFIER_KEYS_DICTIONARY.dni
  })
  const {
    error: patternError,
    value: patternValue,
    onChange: handleChangePattern
  } = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [identifierTypeValue === PROVIDER_IDENTIFIER_KEYS_DICTIONARY.dni && formatDNI].filter(Boolean)
  })
  const cleanDni = useCallback((dni = '') => dni.toLowerCase().replace(/[.-]/g, ''), [])
  const handleAdd = record => () => {
    const found = selectedProviders.some(provider => cleanDni(provider.dni) === cleanDni(record.dni))

    if (found) {
      return
    }

    dispatch(onAddSpecificProviderSuccess({ sellersAdded: selectedProviders.concat(record) }))
  }
  const handleChange = (_, updatedValue) =>
    dispatch(
      onGetSpecificProvidersThunk({
        [PROVIDER_IDENTIFIER_KEYS_DICTIONARY[identifierTypeValue]]: patternValue,
        identifierType: identifierTypeValue,
        page: updatedValue - 1
      })
    )
  const handleRedirect = () => navigate(-1)
  const handleRemove = dni => () => {
    const updatedProviders = selectedProviders.filter(provider => cleanDni(provider.dni) !== cleanDni(dni))

    dispatch(onRemoveSpecificProviderSuccess({ sellersAdded: updatedProviders }))
  }
  const handleSearch = event => {
    event.preventDefault()

    if (patternValue.length < 3) {
      return
    }

    initialStateRef.current = false

    dispatch(
      onGetSpecificProvidersThunk({
        [PROVIDER_IDENTIFIER_KEYS_DICTIONARY[identifierTypeValue]]: patternValue,
        identifierType: identifierTypeValue,
        page: 0
      })
    )
  }
  const disableSubmit = useMemo(() => patternValue.length < 3, [patternValue])

  return (
    <Root>
      <GridContainer padding="0 0 25px">
        <GridItem xs={12}>
          <BackLink onClick={handleRedirect}>
            <BackIcon />
            <BackText>Volver a la Compra Ágil</BackText>
          </BackLink>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 16px">
        <GridItem xs={12}>
          <Title>Selección de proveedores específicos</Title>
          <Description>
            Puedes seleccionar proveedores adicionales que recibirán una notificación individual para cotizar.{' '}
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 21px">
        <GridItem xs={12}>
          <Card>
            <GridContainer spacing={2}>
              <GridItem md={3} xs={12}>
                <Select
                  label="Identificador"
                  options={PROVIDER_IDENTIFIER_OPTIONS}
                  value={identifierTypeValue}
                  onChange={handleChangeIdentifierType}
                />
              </GridItem>
              <GridItem md={9} xs={12}>
                <Form onSubmit={handleSearch}>
                  <GridContainer alignItems="center" spacing={2}>
                    <GridItem md={9} xs={12}>
                      <Input
                        error={patternError}
                        label={PROVIDER_IDENTIFIER_LABELS_DICTIONARY[identifierTypeValue]}
                        value={patternValue}
                        onChange={handleChangePattern}
                      />
                    </GridItem>
                    <GridItem md={3} xs={12}>
                      <Button color="primary" disabled={disableSubmit} type="submit" width="100%">
                        Buscar
                      </Button>
                    </GridItem>
                  </GridContainer>
                </Form>
              </GridItem>
            </GridContainer>
          </Card>
        </GridItem>
      </GridContainer>
      {!loading && (records.length === 0 || totalRecords === 0) && (
        <GridContainer padding="0 0 39px">
          <GridItem xs={12}>
            <Card enableBorder color="blank">
              <GridContainer padding="61px 0 8px">
                <GridItem align="center" xs={12}>
                  <CardImage
                    alt={initialStateRef.current ? 'Búsqueda inicial' : 'Sin resultados'}
                    src="/assets/images/nofound.svg"
                  />
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 8px">
                <GridItem align="center" xs={12}>
                  {initialStateRef.current && <CardTitle>Busca por proveedores</CardTitle>}
                  {!initialStateRef.current && <CardTitle>No se encontraron resultados para tu búsqueda</CardTitle>}
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 48px">
                <GridItem align="center" xs={12}>
                  {initialStateRef.current && (
                    <CardDescription>Intenta con una palabra clave u criterio de búsqueda.</CardDescription>
                  )}
                  {!initialStateRef.current && (
                    <CardDescription>
                      El proveedor que buscas no se encuentra inscrito en Mercado Público. Intenta con otra palabra
                      clave u otro criterio de búsqueda.
                    </CardDescription>
                  )}
                </GridItem>
              </GridContainer>
            </Card>
          </GridItem>
        </GridContainer>
      )}
      {(loading || (records.length > 0 && totalRecords > 0)) && (
        <>
          <GridContainer padding="0 0 25px">
            <GridItem xs={12}>
              {loading && <Skeleton height="23px" width="300px" />}
              {!loading && <Results>{`Se encontraron ${totalRecords} resultados para tu búsqueda`}</Results>}
            </GridItem>
          </GridContainer>
          <GridContainer padding="0 0 10px">
            <GridItem overflowX="auto" xs={12}>
              <Table enableCondensed loading={loading} totalLoadingBars={PROVIDER_ROWS}>
                <TableRow enableHead>
                  <TableCell>RUT</TableCell>
                  <TableCell>Razón social/Nombre de fantasía</TableCell>
                  <TableCell>Seleccionar proveedor</TableCell>
                </TableRow>
                {records.map((record, index) => {
                  const found = selectedProviders.find(provider => cleanDni(provider.dni) === cleanDni(record.dni))

                  return (
                    <TableRow active={!!found} key={`${record.dni}__${index}`}>
                      <TableCell verticalAlign="middle">
                        <RecordText>{record.dni}</RecordText>
                      </TableCell>
                      <TableCell verticalAlign="middle">{record.name}</TableCell>
                      <TableCell verticalAlign="middle" width="229px">
                        <Action active={!!found} onClick={handleAdd(record)}>
                          <ActionIcon active={!!found} />
                          <ActionText active={!!found}>{found ? 'Seleccionado' : 'Seleccionar'}</ActionText>
                          {!!found && <ActionDelete onClick={handleRemove(record.dni)}>Eliminar</ActionDelete>}
                        </Action>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Table>
            </GridItem>
          </GridContainer>
          <GridContainer padding="0 0 39px">
            <GridItem xs={12}>
              {loading && <Skeleton height="46px" width="300px" />}
              {!loading && totalPages > 1 && (
                <Pagination
                  alignment="left"
                  boundaryCount={2}
                  count={totalPages}
                  page={page + 1}
                  onChange={handleChange}
                />
              )}
            </GridItem>
          </GridContainer>
        </>
      )}
      <GridContainer padding="0 0 200px">
        <GridItem xs={12}>
          <BackButton variant="outlined" onClick={handleRedirect}>
            <BackIcon /> &nbsp;&nbsp;Volver a la Compra Ágil
          </BackButton>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default QuoteProvidersList
